import React, { useEffect, useState } from 'react';
import { FaRegTrashAlt } from 'react-icons/fa';
import { ReactBarcode } from 'react-jsbarcode';
import PrimaryButton from './PrimaryButton';
import { formatPhoneNumber } from '../../helpers/utils';
import { useStudentsActions } from '../../contexts/StudentsContext';

type StudentProps = {
  index: number;
  firstName: string;
  lastName: string;
  primaryParentPhone: string;
  secondaryParentPhone: string | undefined | null;
  sessionTime: string;
  sendText: boolean;
  barcode: string;
  flex: number;
  editInProgress: boolean;
  setEditInProgress: (editMode: boolean) => void;
  setOpenDeleteModal: (barcode: string) => void;
  checkInTime: number | undefined | null;
};
const Student = ({
  index,
  firstName,
  lastName,
  primaryParentPhone,
  secondaryParentPhone,
  sessionTime,
  barcode,
  flex,
  editInProgress,
  setEditInProgress,
  setOpenDeleteModal,
  checkInTime,
  sendText,
}: StudentProps) => {
  const [editMode, setEditMode] = useState(false);

  const { removeNthStudent, createStudent, saveStudent, checkInStudent, checkOutStudent } =
    useStudentsActions();

  useEffect(() => {
    if (barcode === '') {
      setEditMode(true);
    } else {
      setEditMode(false);
    }
  }, [barcode]);

  const property = (idLabel: string, label: string, value: string, inputValue?: string | null) => {
    return (
      <div className="py-3 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
        <dt className="text-sm font-medium text-gray-500">{label}</dt>

        {editMode && ((idLabel === 'student-id' && barcode === '') || idLabel !== 'student-id') ? (
          <input
            className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 rounded-md border p-0.5 border-gray-200"
            id={idLabel}
            type="text"
            defaultValue={inputValue ? inputValue : value}
          />
        ) : (
          <dd className="text-sm text-gray-900 sm:mt-0 sm:col-span-2 p-0.5 border border-white mt-auto">
            {value}
          </dd>
        )}
      </div>
    );
  };

  const checkbox = (
    idLabel: string,
    label: string,
    value: boolean,
    inputValue?: boolean | null,
  ) => {
    return (
      <div className="py-3 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
        <dt className="text-sm font-medium text-gray-500">{label}</dt>

        <input
          className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 rounded-md border p-0.5 border-gray-200 mr-auto w-5"
          id={idLabel}
          type="checkbox"
          defaultChecked={inputValue ? inputValue : value}
          disabled={!editMode}
        />
      </div>
    );
  };

  const buttons = () => {
    const saveDisabled = editInProgress && !editMode;
    return (
      <div className="flex flex-wrap mr-1 ml-1" style={{ gap: '2%' }}>
        {editMode ? (
          <button
            className="flex w-full justify-center rounded-md bg-white px-3 py-1.5 text-sm/6 font-semibold border-gray-400 text-black shadow-xs hover:bg-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-200 border mt-2"
            style={{ flex: '49%' }}
            onClick={cancelButton}
          >
            Cancel
          </button>
        ) : (
          <PrimaryButton
            onClick={checkInCheckOut}
            disabled={saveDisabled}
            label={checkInTime ? 'Check out' : 'Check in'}
            style={{ flex: '49%' }}
          />
        )}
        <PrimaryButton
          onClick={saveEditButton}
          disabled={saveDisabled}
          label={editMode ? 'Save' : 'Edit'}
          style={{ flex: '49%' }}
        />
      </div>
    );
  };

  const checkInCheckOut = async () => {
    if (checkInTime) {
      await checkOutStudent(barcode);
    } else {
      await checkInStudent(barcode);
    }
  };

  const cancelButton = () => {
    if (barcode === '') {
      removeNthStudent(index);
    } else {
      toggleEdit();
    }
    setEditInProgress(false);
  };

  const saveEditButton = async () => {
    if (!editMode) {
      setEditInProgress(true);
      toggleEdit();
    } else {
      const data = getDataFromInput();
      if (!changed(data)) {
        toggleEdit();
        setEditInProgress(false);
        return;
      }

      const validity = validateData(data);
      if (validity.valid) {
        const student = {
          firstName: data.firstNameInput,
          lastName: data.lastNameInput,
          primaryParentPhone: data.primaryParentPhoneInput,
          secondaryParentPhone: data.secondaryParentPhoneInput,
          barcode: data.barcodeInput,
          sessionTime: parseInt(data.sessionTimeInput),
          sendText: data.sendTextInput,
        };

        if (barcode === '') {
          await createStudent(student);
        } else {
          await saveStudent(student);
        }
        toggleEdit();
        setEditInProgress(false);
      } else {
        // error with validation
        if (validity?.errors?.includes('firstName')) {
          alert('First name is too long');
        }
        if (validity?.errors?.includes('lastName')) {
          alert('Last name is too long');
        }
        if (validity?.errors?.includes('primaryParentPhone')) {
          alert(
            'Primary phone number is incorrect format (only use numbers, no special characters)',
          );
        }
        if (validity?.errors?.includes('secondaryParentPhone')) {
          alert(
            'Secondary phone number is incorrect format (only use numbers, no special characters)',
          );
        }
        if (validity?.errors?.includes('barcode')) {
          alert('Student ID should be all numbers');
        }
        if (validity?.errors?.includes('sessionTime')) {
          alert('Session time must be a number');
        }
      }
    }
  };

  const getDataFromInput = () => {
    const firstNameInput =
      (document.getElementById('first-name') as HTMLInputElement | undefined)?.value || '';
    const lastNameInput =
      (document.getElementById('last-name') as HTMLInputElement | undefined)?.value || '';
    const primaryParentPhoneInput =
      (document.getElementById('primary-parent-phone') as HTMLInputElement | undefined)?.value ||
      '';
    const secondaryParentPhoneInput =
      (document.getElementById('secondary-parent-phone') as HTMLInputElement | undefined)?.value ||
      '';
    const barcodeInput =
      (document.getElementById('student-id') as HTMLInputElement | undefined)?.value || barcode;
    const sessionTimeInput =
      (document.getElementById('session-time') as HTMLInputElement | undefined)?.value || '30';
    const sendTextInput =
      (document.getElementById('send-text') as HTMLInputElement | undefined)?.checked || false;

    return {
      firstNameInput,
      lastNameInput,
      primaryParentPhoneInput,
      secondaryParentPhoneInput,
      barcodeInput,
      sessionTimeInput,
      sendTextInput,
    };
  };

  const changed = (data: {
    firstNameInput: string;
    lastNameInput: string;
    primaryParentPhoneInput: string;
    secondaryParentPhoneInput: string;
    barcodeInput: string;
    sessionTimeInput: string;
    sendTextInput: boolean;
  }): boolean => {
    return !(
      firstName === data.firstNameInput &&
      lastName === data.lastNameInput &&
      primaryParentPhone === data.primaryParentPhoneInput &&
      secondaryParentPhone === data.secondaryParentPhoneInput &&
      barcode === data.barcodeInput &&
      sessionTime === data.sessionTimeInput &&
      sendText === data.sendTextInput
    );
  };

  const validateData = ({
    firstNameInput,
    lastNameInput,
    primaryParentPhoneInput,
    secondaryParentPhoneInput,
    barcodeInput,
    sessionTimeInput,
  }: {
    firstNameInput: string;
    lastNameInput: string;
    primaryParentPhoneInput: string;
    secondaryParentPhoneInput: string;
    barcodeInput: string;
    sessionTimeInput: string;
  }): { valid: boolean; errors?: string[] } => {
    const errors = [];
    if (firstNameInput.length > 30) {
      errors.push('firstName');
    }
    if (lastNameInput.length > 30) {
      errors.push('lastName');
    }
    if (primaryParentPhoneInput.length < 10 || !primaryParentPhoneInput.match(/^[0-9]+$/)) {
      errors.push('primaryParentPhone');
    }
    if (
      secondaryParentPhoneInput &&
      (secondaryParentPhoneInput.length < 10 || !secondaryParentPhoneInput.match(/^[0-9]+$/))
    ) {
      errors.push('secondaryParentPhone');
    }
    if (!barcodeInput.match(/^[0-9]+$/)) {
      errors.push('barcode');
    }
    if (!sessionTimeInput.match(/^[0-9]+$/)) {
      errors.push('sessionTime');
    }
    if (errors.length > 0) {
      return { valid: false, errors };
    } else {
      return { valid: true };
    }
  };

  const toggleEdit = () => {
    setEditMode(!editMode);
  };

  return (
    <div
      className="bg-white overflow-hidden shadow rounded-lg border mb-8"
      style={{ flex: `${flex}%`, maxWidth: `${flex}%` }}
    >
      <div className="px-4 py-5 sm:px-6 flex justify-between">
        <div>
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {barcode === '' ? 'New Student' : `${firstName} ${lastName}`}
          </h3>
          {barcode === '' ? null : (
            <ReactBarcode value={barcode} options={{ format: 'code128', height: 40 }} />
          )}
        </div>
        <div
          className="mt-auto mb-auto hover:cursor-pointer"
          onClick={() => setOpenDeleteModal(barcode)}
        >
          <FaRegTrashAlt color="#FF3D00" className="mr-auto ml-auto" />
        </div>
      </div>
      <div className="border-t border-gray-200 px-4 py-5 sm:p-0 mb-4">
        <dl className="sm:divide-y sm:divide-gray-200">
          {property('first-name', 'First name', firstName)}
          {property('last-name', 'Last name', lastName)}
          {property(
            'primary-parent-phone',
            'Primary parent phone number',
            formatPhoneNumber(primaryParentPhone),
            primaryParentPhone,
          )}
          {property(
            'secondary-parent-phone',
            'Secondary parent phone number',
            formatPhoneNumber(secondaryParentPhone),
            secondaryParentPhone,
          )}
          {property('session-time', 'Session time', `${sessionTime} minutes`, sessionTime)}
          {property('student-id', 'Student ID', barcode)}
          {checkbox('send-text', 'Send text?', sendText, sendText)}
          {buttons()}
        </dl>
      </div>
    </div>
  );
};

export default Student;
